import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFileUpload } from "@/lib/file-storage/hooks";
import { useToast } from "@/components/ui/use-toast";
import type { PresignedClient, UploadedFile } from "@/lib/file-storage/types";

interface QuestionnaireAttachmentUploadProps {
  name: string;
  presignedClient: PresignedClient;
  onFileUploaded: (file: File, stored: UploadedFile) => void;
  /** Górny limit plików w jednym wsadzie (domyślnie `ATTACHMENT_MAX_FILES_AT_ONCE`); sekcja skanów zawęża go do wolnych miejsc */
  maxFiles?: number;
  /** Opis przyczyny błędu doklejany do nazwy pliku w toaście; `null` = sama nazwa (zachowanie ankiety) */
  describeUploadError?: (error: unknown) => string | null;
  /** Raz po całym wsadzie (walidacja wsadu, która nic nie wysłała, go nie wywołuje): jeden toast, jedno odświeżenie */
  onBatchFinished?: (result: {
    uploaded: number;
    failedUploads: { file: File; error: unknown }[];
  }) => void;
}

export const useQuestionnaireAttachmentUpload = ({
  name,
  presignedClient,
  onFileUploaded,
  maxFiles,
  describeUploadError,
  onBatchFinished,
}: QuestionnaireAttachmentUploadProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingUpload, setPendingUpload] = useState<File[] | null>(null);

  const handleUploadErrors = useCallback(
    (failedUploads: { file: File; error: unknown }[]) => {
      if (failedUploads.length === 0) return;

      // Użytkownik dostaje tłumaczony opis; surowy błąd (angielski `message` backendu, wyjątek sieci)
      // zostaje w konsoli dla wsparcia.
      console.error(
        "Attachment upload failed:",
        failedUploads.map(({ file, error }) => ({ file: file.name, error })),
      );
      const fileNames = failedUploads.map(({ file, error }) => {
        const reason = describeUploadError?.(error);
        return reason ? `• ${file.name}: ${reason}` : `• ${file.name}`;
      });
      const description = fileNames.join("\n");

      toast({
        title: t("fileAttachments.uploadErrorTitle"),
        description,
        variant: "destructive",
        // Kilku linii nie da się przeczytać w domyślne 5 s.
        ...(failedUploads.length > 1 && { duration: 10000 }),
      });
    },
    [describeUploadError, t, toast],
  );

  const onUploadError = useCallback(
    (file: File, error: unknown) => {
      handleUploadErrors([{ file, error }]);
    },
    [handleUploadErrors],
  );

  const validateOptions = useMemo(
    () => (maxFiles === undefined ? undefined : { maxFiles }),
    [maxFiles],
  );

  const { uploadFiles: uploadFilesInternal, operationProgress } = useFileUpload(
    {
      client: presignedClient,
      fieldName: name,
      validateOptions,
      onUploadSuccess: onFileUploaded,
      onUploadError,
    },
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      if (operationProgress.isBusy()) return;

      const validFiles = files.filter((file) => file.size > 0);
      if (validFiles.length === 0) return;

      const { failedUploads, uploadedCount, validationError } =
        await uploadFilesInternal(validFiles);

      if (validationError) {
        toast({
          title: t("fileAttachments.fileReadErrorTitle"),
          description: validationError,
          variant: "destructive",
        });
        return;
      }

      handleUploadErrors(failedUploads);
      onBatchFinished?.({ uploaded: uploadedCount, failedUploads });
    },
    [
      uploadFilesInternal,
      handleUploadErrors,
      onBatchFinished,
      toast,
      t,
      operationProgress.isBusy,
    ],
  );

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const filesArray: File[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (file) {
          filesArray.push(file);
        }
      }
      event.target.value = "";

      if (filesArray.length > 1) {
        setPendingUpload(filesArray);
        return;
      }
      await uploadFiles(filesArray);
    },
    [uploadFiles],
  );

  const confirmUpload = useCallback(async () => {
    if (!pendingUpload) return;
    const filesToUpload = pendingUpload;
    setPendingUpload(null);
    await uploadFiles(filesToUpload);
  }, [pendingUpload, uploadFiles]);

  const cancelUpload = useCallback(() => setPendingUpload(null), []);

  const handleAddFilesClick = useCallback(() => {
    if (operationProgress.isBusy()) return;
    fileInputRef.current?.click();
  }, [operationProgress.isBusy]);

  const handleDrop = useCallback(
    async (files: FileList | File[]) => {
      if (operationProgress.isBusy()) return;
      const filesArray = Array.from(files);
      if (filesArray.length === 0) return;
      if (filesArray.length > 1) {
        setPendingUpload(filesArray);
        return;
      }
      await uploadFiles(filesArray);
    },
    [uploadFiles, operationProgress.isBusy],
  );

  return {
    fileInputRef,
    operationProgress,
    pendingUpload,
    confirmUpload,
    cancelUpload,
    handleAddFilesClick,
    handleFileUpload,
    handleDrop,
  };
};
